/**
 * InstanceRow Component
 *
 * Displays a single task instance in the batch tree with:
 * - Tree branch lines (├─, └─)
 * - Trigger type badge (root/hook/cron)
 * - Status with color coding
 * - Progress bar when running
 */

import type { Instance } from "@/types/dataflow";
import { TaskTypeBadge } from "@/components/dataflow/TaskTypeBadge";
import { Loader2 } from "lucide-react";

interface InstanceRowProps {
  instance: Instance;
  level: number;
  isLast: boolean;
  isSelected: boolean;
  onSelect: (id: number) => void;
}

export function InstanceRow({
  instance,
  level,
  isLast,
  isSelected,
  onSelect,
}: InstanceRowProps) {
  const progressPercent = instance.progress_total && instance.progress_total > 0
    ? Math.round(((instance.progress_current || 0) / instance.progress_total) * 100)
    : Math.round(instance.progress_pct || 0);

  return (
    <div
      className={`
        flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm
        transition-colors hover:bg-white/[0.03] rounded-md mx-1 my-0.5
        ${isSelected ? "bg-white/[0.06]" : ""}
      `}
      onClick={() => onSelect(instance.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onSelect(instance.id);
        }
      }}
    >
      {/* Tree indentation with visual guides */}
      <div className="flex items-center">
        {/* Indentation with vertical guide lines */}
        {Array.from({ length: level }).map((_, i) => (
          <div key={i} className="w-4 flex justify-center">
            <div className="w-px h-full bg-[var(--border)]/30" />
          </div>
        ))}
        {/* Branch connector */}
        <div className="w-4 flex justify-center">
          <span className="font-mono text-[var(--muted)]/60 select-none text-xs">
            {isLast ? "└─" : "├─"}
          </span>
        </div>
      </div>

      {/* Trigger type badge */}
      <TriggerBadge type={(instance as any).trigger_type || "manual"} isRoot={true} />

      {/* Instance ID and task type */}
      <span className="text-[var(--muted)] w-12">#{instance.id}</span>
      <div className="min-w-[140px]">
        <TaskTypeBadge taskType={instance.task_type} />
      </div>

      {/* Status with color coding */}
      <StatusBadge status={instance.status} />

      {/* Progress bar (only when running) */}
      {instance.status === "RUNNING" && progressPercent > 0 && (
        <div className="flex flex-1 items-center gap-2">
          <div className="h-1.5 flex-1 max-w-[120px] rounded-full bg-[var(--border)] overflow-hidden">
            <div
              className="h-full rounded-full bg-sky-400 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-[var(--muted)]">
            {instance.progress_total && instance.progress_total > 0
              ? `${instance.progress_current || 0}/${instance.progress_total}`
              : `${progressPercent}%`}
          </span>
          <Loader2 className="size-3 animate-spin text-sky-400" />
        </div>
      )}

      {/* Scope */}
      <span className="text-xs text-[var(--muted)] ml-auto">{instance.scope}</span>
    </div>
  );
}

interface TriggerBadgeProps {
  type: "cron" | "hook" | "manual";
  isRoot: boolean;
}

function TriggerBadge({ type, isRoot }: TriggerBadgeProps) {
  const labels: Record<string, string> = {
    cron: "CRON",
    hook: "HOOK",
    manual: "MANUAL",
  };

  const colors: Record<string, string> = {
    cron: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    hook: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    manual: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  };

  return (
    <span
      className={`
        inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium
        rounded border ${colors[type]}
      `}
      title={isRoot ? "Root instance" : "Child instance"}
    >
      {isRoot ? "ROOT" : labels[type]}
    </span>
  );
}

interface StatusBadgeProps {
  status: Instance["status"];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const colors: Record<string, string> = {
    QUEUED: "bg-slate-500/15 text-slate-400",
    RUNNING: "bg-sky-500/15 text-sky-400",
    COMPLETED: "bg-emerald-500/15 text-emerald-400",
    FAILED: "bg-rose-500/15 text-rose-400",
    CANCELLED: "bg-orange-500/15 text-orange-400",
    PENDING: "bg-slate-500/15 text-slate-400",
  };

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 text-[11px] font-medium
        rounded-full ${colors[status]}
      `}
    >
      {status}
    </span>
  );
}
